import React, { useState, useContext } from "react";
import API from "../api";
import { AuthContext } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Briefcase,
  MapPin,
  DollarSign,
  CheckCircle2,
  Loader2,
  User2,
  Building2,
  CalendarDays,
  BadgeIndianRupee,
  FileText,
  Globe,
} from "lucide-react";

const services = [
  "Web Development",
  "Mobile App Development",
  "UI/UX Design",
  "SEO",
  "Digital Marketing",
  "Software Development",
  "Cloud Computing",
  "AI & Machine Learning",
  "Data Analytics",
  "Cybersecurity",
  "DevOps Services",
  "E-commerce Development",
  "CRM Development",
  "ERP Solutions",
  "Blockchain Development",
  "IT Consulting",
  "Business Email",
];

const clientTypes = [
  { label: "Lead", value: "lead" },
  { label: "Contact", value: "contact" },
  { label: "Client", value: "client" },
];

const AddClient = () => {
  const navigate = useNavigate();
  const { user, orgSlug, loading: authLoading } =
    useContext(AuthContext);

  const [form, setForm] = useState({
    name: "",
    contactEmail: "",
    phone: "",
    company: "",
    industry: "",
    serviceNeeded: "",
    address: "",
    city: "",
    district: "",
    state: "",
    country: "India",
    pincode: "",
    description: "",
    projectStartDate: "",
    projectDueDate: "",
    revenue: "",
    website: "",
    status: "lead",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const validatePhone = (phone) => /^[6-9]\d{9}$/.test(phone);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePhone(form.phone)) {
      return alert("Enter valid 10 digit phone number");
    }

    if (!user) {
      return alert("Authentication error");
    }

    try {
      setIsSubmitting(true);

      const payload = {
        ...form,
        revenue: form.revenue ? parseFloat(form.revenue) : 0,
      };
       
      console.log("PAYLOAD:", payload);
      await API.post("clients/create/", payload);
      
      alert("Client Created Successfully!");

      navigate(`/${orgSlug}/clients`, {
        replace: true,
      });
    } catch (error) {
      console.error(error);

      alert(
        error?.response?.data?.error ||
          "Failed to create client"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading || !orgSlug) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-50">
        <Loader2
          className="animate-spin text-indigo-600"
          size={40}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">

        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
          <div>
            <Link
              to={`/${orgSlug}/clients`}
              className="group flex items-center text-slate-500 hover:text-indigo-600 text-sm font-semibold transition-colors mb-3"
            >
              <ArrowLeft
                size={16}
                className="mr-2 group-hover:-translate-x-1 transition-transform"
              />
              Back To Clients
            </Link>

            <h1 className="text-4xl font-black tracking-tight text-slate-900">
              Add New Client
            </h1>

            <p className="text-slate-500 mt-2 font-medium">
              Create and manage enterprise client records.
            </p>
          </div>

          <div className="hidden md:flex items-center gap-3 bg-indigo-50 border border-indigo-100 px-5 py-3 rounded-2xl">
            <Building2 className="text-indigo-600" size={22} />
            <div>
              <p className="text-xs uppercase tracking-widest text-indigo-500 font-black">
                Organization
              </p>
              <p className="font-bold text-slate-800">
                {orgSlug}
              </p>
            </div>
          </div>
        </div>

        {/* FORM */}
        <form onSubmit={handleSubmit} className="space-y-8">

          {/* CLIENT INFO */}
          <SectionCard
            icon={<User2 size={18} />}
            title="Client Information"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              <Input
                label="Client Name"
                name="name"
                placeholder="John Doe"
                value={form.name}
                onChange={handleChange}
                required
              />

              <Input
                label="Work Email"
                type="email"
                name="contactEmail"
                placeholder="john@company.com"
                value={form.contactEmail}
                onChange={handleChange}
                required
              />

              <Input
                label="Phone Number"
                name="phone"
                placeholder="9876543210"
                value={form.phone}
                onChange={handleChange}
                required
              />

              <Input
                label="Company Name"
                name="company"
                placeholder="SK Groups"
                value={form.company}
                onChange={handleChange}
              />

              <Input
                label="Industry"
                name="industry"
                placeholder="Technology"
                value={form.industry}
                onChange={handleChange}
              />

              <Input
                label="Website"
                name="website"
                placeholder="https://example.com"
                value={form.website}
                onChange={handleChange}
              />

              <div className="md:col-span-2">
                <Select
                  label="Service Needed"
                  name="serviceNeeded"
                  value={form.serviceNeeded}
                  options={services}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </SectionCard>

          {/* LOCATION */}
          <SectionCard
            icon={<MapPin size={18} />}
            title="Location Details"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

              <div className="md:col-span-3">
                <Input
                  label="Full Address"
                  name="address"
                  placeholder="123 Business Park"
                  value={form.address}
                  onChange={handleChange}
                />
              </div>

              <Input
                label="City"
                name="city"
                value={form.city}
                onChange={handleChange}
              />

              <Input
                label="District"
                name="district"
                value={form.district}
                onChange={handleChange}
              />

              <Input
                label="State"
                name="state"
                value={form.state}
                onChange={handleChange}
              />

              <Input
                label="Country"
                name="country"
                value={form.country}
                onChange={handleChange}
              />

              <Input
                label="Pincode"
                name="pincode"
                value={form.pincode}
                onChange={handleChange}
              />
            </div>
          </SectionCard>

          {/* PROJECT */}
          <SectionCard
            icon={<Briefcase size={18} />}
            title="Project & Financials"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              <Input
                label="Estimated Revenue"
                type="number"
                name="revenue"
                placeholder="50000"
                value={form.revenue}
                onChange={handleChange}
              />

              <Select
                label="Client Type"
                name="status"
                value={form.status}
                options={clientTypes}
                onChange={handleChange}
              />

              <Input
                label="Project Start Date"
                type="date"
                name="projectStartDate"
                value={form.projectStartDate}
                onChange={handleChange}
              />

              <Input
                label="Project Due Date"
                type="date"
                name="projectDueDate"
                value={form.projectDueDate}
                onChange={handleChange}
              />

              <div className="md:col-span-2">
                <label className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 mb-2 block">
                  Project Description
                </label>

                <textarea
                  name="description"
                  rows={5}
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Explain the project requirements..."
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all resize-none"
                />
              </div>
            </div>
          </SectionCard>

          {/* SUMMARY */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-3xl p-8 text-white shadow-xl shadow-indigo-100"
          >
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle2 size={24} />
              <h3 className="text-xl font-black tracking-tight">
                Ready To Create Client
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

              <SummaryCard
                icon={<Building2 size={18} />}
                title="Company"
                value={form.company || "Not Added"}
              />

              <SummaryCard
                icon={<BadgeIndianRupee size={18} />}
                title="Revenue"
                value={
                  form.revenue
                    ? `₹${Number(form.revenue).toLocaleString(
                        "en-IN"
                      )}`
                    : "₹0"
                }
              />

              <SummaryCard
                icon={<CalendarDays size={18} />}
                title="Client Type"
                value={form.status}
              />
            </div>
          </motion.div>

          {/* ACTION BUTTONS */}
          <div className="flex flex-col sm:flex-row items-center justify-end gap-4 pb-10">

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-full sm:w-auto px-8 py-3 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 font-bold transition-all"
            >
              Cancel
            </button>

            <motion.button
              whileTap={{ scale: 0.98 }}
              disabled={isSubmitting}
              type="submit"
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white px-10 py-3 rounded-2xl font-bold shadow-lg shadow-indigo-200 transition-all"
            >
              {isSubmitting ? (
                <>
                  <Loader2
                    className="animate-spin"
                    size={18}
                  />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle2 size={18} />
                  Create Client
                </>
              )}
            </motion.button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* =========================
   REUSABLE COMPONENTS
========================= */

const SectionCard = ({ icon, title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm"
  >
    <div className="flex items-center gap-3 mb-8 border-b border-slate-100 pb-4">
      <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600">
        {icon}
      </div>

      <div>
        <h2 className="text-sm font-black uppercase tracking-[0.2em] text-slate-700">
          {title}
        </h2>
      </div>
    </div>

    {children}
  </motion.div>
);

const Input = ({ label, ...props }) => (
  <div className="flex flex-col">
    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-2 ml-1">
      {label}
    </label>

    <input
      {...props}
      className="w-full px-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-semibold text-slate-700 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all placeholder:text-slate-400"
    />
  </div>
);

const Select = ({
  label,
  options,
  ...props
}) => (
  <div className="flex flex-col">
    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-2 ml-1">
      {label}
    </label>

    <select
      {...props}
      className="w-full px-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-semibold text-slate-700 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all"
    >
      {options.map((option, index) => {

        // STRING ARRAY
        if (typeof option === "string") {
          return (
            <option
              key={index}
              value={option}
            >
              {option}
            </option>
          );
        }

        // OBJECT ARRAY
        return (
          <option
            key={option.value}
            value={option.value}
          >
            {option.label}
          </option>
        );
      })}
    </select>
  </div>
);
const SummaryCard = ({ icon, title, value }) => (
  <div className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-2xl p-5">
    <div className="flex items-center gap-2 text-indigo-100 mb-3">
      {icon}
      <span className="text-xs uppercase tracking-widest font-black">
        {title}
      </span>
    </div>

    <h4 className="text-lg font-black tracking-tight">
      {value}
    </h4>
  </div>
);

export default AddClient;